import {
  adminUserListResponseSchema,
  adminUserSchema,
  agentDetailResponseSchema,
  agentListResponseSchema,
  agentRunListResponseSchema,
  agentRunSchema,
  analyticsRollupSchema,
  apiKeyListResponseSchema,
  assignAgentRequestSchema,
  assignIssueRequestSchema,
  authConfigResponseSchema,
  authMeResponseSchema,
  authSessionResponseSchema,
  type CreateIssueRequest,
  changeIssueStatusRequestSchema,
  chaptersResponseSchema,
  clientSchema,
  clientsResponseSchema,
  contextUsersResponseSchema,
  createAdminUserRequestSchema,
  createAgentRunRequestSchema,
  createApiKeyRequestSchema,
  createApiKeyResponseSchema,
  createArtifactRequestSchema,
  createChapterRequestSchema,
  createClientRequestSchema,
  createCommentRequestSchema,
  createCycleRequestSchema,
  createIssueRequestSchema,
  createProductRequestSchema,
  createProjectRequestSchema,
  createResearchTrackRequestSchema,
  createRoadmapMilestoneRequestSchema,
  cyclesResponseSchema,
  type DemoCleanupRequest,
  demoCleanupPreviewSchema,
  demoCleanupRequestSchema,
  demoCleanupResultSchema,
  devLoginRequestSchema,
  githubBranchSuggestionRequestSchema,
  githubBranchSuggestionResponseSchema,
  githubSettingsResponseSchema,
  type Issue,
  inboxNotificationSchema,
  inboxResponseSchema,
  issueDetailResponseSchema,
  issueLabelsResponseSchema,
  issueListFiltersSchema,
  issueListResponseSchema,
  linkGithubPullRequestRequestSchema,
  maintenanceOverviewSchema,
  preProvisionAdminUserRequestSchema,
  productSchema,
  productsResponseSchema,
  projectSchema,
  projectsResponseSchema,
  researchTrackSchema,
  researchTracksResponseSchema,
  roadmapMilestoneSchema,
  roadmapResponseSchema,
  type SessionUserDto,
  slackSettingsResponseSchema,
  updateAdminUserRequestSchema,
  updateAgentRunRequestSchema,
  updateChapterRequestSchema,
  updateClientRequestSchema,
  updateCycleRequestSchema,
  updateGithubSettingsRequestSchema,
  updateIssueRequestSchema,
  updateProductRequestSchema,
  updateProjectRequestSchema,
  updateResearchTrackRequestSchema,
  updateRoadmapMilestoneRequestSchema,
  updateSlackSettingsRequestSchema,
} from "@qualtio-workhub/contracts";

export type AuthConfig = ReturnType<typeof authConfigResponseSchema.parse>;
export type DemoCleanupPreview = ReturnType<typeof demoCleanupPreviewSchema.parse>;

export async function getMaintenanceOverview(token: string) {
  return getJson(
    "/settings/maintenance",
    token,
    maintenanceOverviewSchema,
    "maintenance_status_failed",
  );
}

export async function previewDemoCleanup(token: string): Promise<DemoCleanupPreview> {
  return getJson(
    "/settings/maintenance/demo/preview",
    token,
    demoCleanupPreviewSchema,
    "cleanup_preview_failed",
  );
}

export async function applyDemoCleanup(token: string, input: DemoCleanupRequest) {
  return sendJsonAndParse("/settings/maintenance/demo/cleanup", {
    token,
    input,
    method: "POST",
    requestSchema: demoCleanupRequestSchema,
    responseSchema: demoCleanupResultSchema,
    errorCode: "cleanup_failed",
  });
}
export type SessionUser = SessionUserDto;
export type AdminUser = ReturnType<typeof adminUserListResponseSchema.parse>["items"][number];
export type IssueDetail = ReturnType<typeof issueDetailResponseSchema.parse>;
export type InboxNotification = ReturnType<typeof inboxResponseSchema.parse>["items"][number];
export type Agent = ReturnType<typeof agentListResponseSchema.parse>["items"][number];
export type AgentRun = ReturnType<typeof agentRunSchema.parse>;
export type AgentDetail = ReturnType<typeof agentDetailResponseSchema.parse>;
export type AgentRunDetail = {
  run: AgentRun;
  issue: Issue;
  agent: Agent | null;
  artifacts: IssueDetail["artifacts"];
  activity: IssueDetail["activity"];
};
export type GithubSettingsState = ReturnType<typeof githubSettingsResponseSchema.parse>;
export type SlackSettingsState = ReturnType<typeof slackSettingsResponseSchema.parse>;
export type GithubPullRequestLink = IssueDetail["githubPullRequests"][number];
export type Product = ReturnType<typeof productsResponseSchema.parse>["items"][number];
export type Client = ReturnType<typeof clientsResponseSchema.parse>["items"][number];
export type Project = ReturnType<typeof projectsResponseSchema.parse>["items"][number];
export type ResearchTrack = ReturnType<typeof researchTracksResponseSchema.parse>["items"][number];
export type Chapter = ReturnType<typeof chaptersResponseSchema.parse>["items"][number];
export type Cycle = ReturnType<typeof cyclesResponseSchema.parse>["items"][number];
export type RoadmapMilestone = ReturnType<typeof roadmapResponseSchema.parse>["items"][number];
export type ContextUser = ReturnType<typeof contextUsersResponseSchema.parse>["items"][number];
export type IssueLabel = ReturnType<typeof issueLabelsResponseSchema.parse>["items"][number];
export type AnalyticsRollup = ReturnType<typeof analyticsRollupSchema.parse>;
export type ApiKey = ReturnType<typeof apiKeyListResponseSchema.parse>["items"][number];
export type IssueFilters = Partial<{
  status: Issue["status"];
  priority: Issue["priority"];
  assigneeUserId: string;
  assigneeAgentId: string;
  chapterId: string;
  productId: string;
  clientAccountId: string;
  projectId: string;
  researchTrackId: string;
  cycleId: string;
  roadmapMilestoneId: string;
  q: string;
}>;

const apiBaseUrl = import.meta.env["VITE_WORKHUB_API_URL"] ?? "/api";
const tokenStorageKey = "qualtio-workhub-token";
export const browserSessionToken = "browser-cookie-session";
const jsonHeaders = { "content-type": "application/json" };
const issueItemSchema = issueListResponseSchema.shape.items.element;
const chapterItemSchema = chaptersResponseSchema.shape.items.element;
const cycleItemSchema = cyclesResponseSchema.shape.items.element;

type Parser<T> = { parse(input: unknown): T };
type QueryValue = string | number | null | undefined;
type JsonRequestOptions<TInput, TOutput> = {
  errorCode: string;
  input: TInput;
  method: "PATCH" | "POST";
  requestSchema: Parser<TInput>;
  responseSchema: Parser<TOutput>;
  token: string | null;
};
type JsonRequestWithoutResponseOptions<TInput> = {
  errorCode: string;
  input: TInput;
  method: "PATCH" | "POST";
  requestSchema: Parser<TInput>;
  token: string | null;
};

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly serverMessage?: string,
  ) {
    super(serverMessage ?? (status === 403 ? "forbidden" : code));
    this.name = "ApiError";
  }
}

export function isForbiddenError(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 403;
}

export function readStoredToken(): string | null {
  localStorage.removeItem(tokenStorageKey);
  return null;
}

export function writeStoredToken(_token: string | null): void {
  localStorage.removeItem(tokenStorageKey);
}

export function getGoogleLoginUrl(): string {
  return `${apiBaseUrl}/auth/google/start`;
}

export async function getAuthConfig(): Promise<AuthConfig> {
  return getJson("/auth/config", null, authConfigResponseSchema, "auth_config_failed");
}

export async function getCurrentUser(token: string | null): Promise<SessionUser | null> {
  const response = await apiRequest("/auth/me", { token });
  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error("auth_me_failed");
  }

  const parsed = authMeResponseSchema.parse(await response.json());
  return parsed.user;
}

export async function devLogin(input: { email: string; password: string }): Promise<{
  user: SessionUser;
  token: string | null;
}> {
  const body = devLoginRequestSchema.parse(input);
  const response = await apiRequest("/auth/dev-login", {
    body: JSON.stringify(body),
    headers: { "content-type": "application/json" },
    method: "POST",
    token: null,
  });

  if (!response.ok) {
    throw new Error("dev_login_failed");
  }

  const parsed = authSessionResponseSchema.parse(await response.json());
  return { user: parsed.user, token: browserSessionToken };
}

export async function logout(token: string | null): Promise<void> {
  await apiRequest("/auth/logout", { method: "POST", token });
}

export async function listIssues(token: string, filters: IssueFilters = {}): Promise<Issue[]> {
  const parsedFilters = issueListFiltersSchema.partial().parse(filters);
  return (
    await getJson(
      buildQueryPath("/issues", parsedFilters),
      token,
      issueListResponseSchema,
      "issues_list_failed",
    )
  ).items;
}

export async function createIssue(token: string, input: CreateIssueRequest): Promise<Issue> {
  return sendJsonAndParse("/issues", {
    errorCode: "issue_create_failed",
    input,
    method: "POST",
    requestSchema: createIssueRequestSchema,
    responseSchema: issueItemSchema,
    token,
  });
}

export async function getIssueDetail(token: string, key: string): Promise<IssueDetail> {
  return getJson(
    `/issues/${encodeURIComponent(key)}`,
    token,
    issueDetailResponseSchema,
    "issue_detail_failed",
  );
}

export async function updateIssue(
  token: string,
  key: string,
  input: Partial<CreateIssueRequest>,
): Promise<Issue> {
  return sendJsonAndParse(`/issues/${encodeURIComponent(key)}`, {
    errorCode: "issue_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateIssueRequestSchema,
    responseSchema: issueItemSchema,
    token,
  });
}

export async function deleteIssue(token: string, key: string): Promise<void> {
  await deleteRequest(`/issues/${encodeURIComponent(key)}`, token, "issue_delete_failed");
}

export async function addIssueComment(
  token: string,
  key: string,
  input: { body: string },
): Promise<void> {
  await sendJson(`/issues/${encodeURIComponent(key)}/comments`, {
    errorCode: "issue_comment_failed",
    input,
    method: "POST",
    requestSchema: createCommentRequestSchema,
    token,
  });
}

export async function changeIssueStatus(
  token: string,
  key: string,
  input: { status: Issue["status"]; blockedReason?: string; doneSummary?: string },
): Promise<Issue> {
  return sendJsonAndParse(`/issues/${encodeURIComponent(key)}/status`, {
    errorCode: "issue_status_failed",
    input,
    method: "POST",
    requestSchema: changeIssueStatusRequestSchema,
    responseSchema: issueItemSchema,
    token,
  });
}

export async function assignIssueAgent(
  token: string,
  key: string,
  input: { assigneeAgentId: string | null },
): Promise<Issue> {
  return sendJsonAndParse(`/issues/${encodeURIComponent(key)}/assign-agent`, {
    errorCode: "issue_agent_assign_failed",
    input,
    method: "POST",
    requestSchema: assignAgentRequestSchema,
    responseSchema: issueItemSchema,
    token,
  });
}

export async function listInbox(
  token: string,
  options: { includeRead?: boolean } = {},
): Promise<InboxNotification[]> {
  return (
    await getJson(
      buildQueryPath("/inbox", { includeRead: options.includeRead === true ? "true" : undefined }),
      token,
      inboxResponseSchema,
      "inbox_failed",
    )
  ).items;
}

export async function markInboxNotificationRead(
  token: string,
  id: string,
): Promise<InboxNotification> {
  return postAndParse(
    `/inbox/${encodeURIComponent(id)}/read`,
    token,
    inboxNotificationSchema,
    "inbox_mark_read_failed",
  );
}

export async function markInboxNotificationUnread(
  token: string,
  id: string,
): Promise<InboxNotification> {
  return postAndParse(
    `/inbox/${encodeURIComponent(id)}/unread`,
    token,
    inboxNotificationSchema,
    "inbox_mark_unread_failed",
  );
}

export async function listAgents(token: string): Promise<Agent[]> {
  return (await getJson("/agents", token, agentListResponseSchema, "agents_failed")).items;
}

export async function getAgentDetail(token: string, id: string): Promise<AgentDetail> {
  return getJson(
    `/agents/${encodeURIComponent(id)}`,
    token,
    agentDetailResponseSchema,
    "agent_detail_failed",
  );
}

export async function listAgentRuns(
  token: string,
  filters: Partial<{ issueId: string; agentId: string; status: AgentRun["status"] }> = {},
): Promise<AgentRun[]> {
  return (
    await getJson(
      buildQueryPath("/agents/runs", filters),
      token,
      agentRunListResponseSchema,
      "agent_runs_failed",
    )
  ).items;
}

export async function getAgentRunDetail(token: string, id: string): Promise<AgentRunDetail> {
  const response = await apiRequest(`/agents/runs/${encodeURIComponent(id)}`, { token });
  await assertOk(response, "agent_run_detail_failed");
  return (await response.json()) as AgentRunDetail;
}

export async function createAgentRun(
  token: string,
  input: { issueId: string; agentId: string; source: AgentRun["source"]; goal: string },
): Promise<AgentRun> {
  return sendJsonAndParse("/agents/runs", {
    errorCode: "agent_run_create_failed",
    input,
    method: "POST",
    requestSchema: createAgentRunRequestSchema,
    responseSchema: agentRunSchema,
    token,
  });
}

export async function updateAgentRun(
  token: string,
  id: string,
  input: Partial<{
    status: AgentRun["status"];
    summary: string;
    error: string;
    sessionRef: string;
    branchName: string;
    pullRequestUrl: string;
  }>,
): Promise<AgentRun> {
  return sendJsonAndParse(`/agents/runs/${encodeURIComponent(id)}`, {
    errorCode: "agent_run_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateAgentRunRequestSchema,
    responseSchema: agentRunSchema,
    token,
  });
}

export async function attachIssueArtifact(
  token: string,
  key: string,
  input: {
    type: "pull_request" | "commit" | "log" | "test_result" | "screenshot" | "file" | "note";
    title: string;
    url: string | null;
    body: string | null;
    agentRunId?: string;
  },
): Promise<void> {
  await sendJson(`/issues/${encodeURIComponent(key)}/artifacts`, {
    errorCode: "artifact_create_failed",
    input,
    method: "POST",
    requestSchema: createArtifactRequestSchema,
    token,
  });
}

export async function getGithubSettings(token: string): Promise<GithubSettingsState> {
  return getJson("/settings/github", token, githubSettingsResponseSchema, "github_settings_failed");
}

export async function updateGithubSettings(
  token: string,
  input: GithubSettingsState["settings"],
): Promise<GithubSettingsState> {
  return sendJsonAndParse("/settings/github", {
    errorCode: "github_settings_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateGithubSettingsRequestSchema,
    responseSchema: githubSettingsResponseSchema,
    token,
  });
}

export async function syncGithubRepositories(token: string): Promise<void> {
  const response = await apiRequest("/settings/github/sync", { method: "POST", token });
  await assertOk(response, "github_sync_failed");
}

export async function suggestGithubBranch(
  token: string,
  input: { issueKey: string; title: string },
): Promise<string> {
  return (
    await sendJsonAndParse("/github/branch-suggestion", {
      errorCode: "github_branch_failed",
      input,
      method: "POST",
      requestSchema: githubBranchSuggestionRequestSchema,
      responseSchema: githubBranchSuggestionResponseSchema,
      token,
    })
  ).branchName;
}

export async function linkGithubPullRequest(
  token: string,
  key: string,
  input: { url: string; repositoryId?: string; agentRunId?: string },
): Promise<GithubPullRequestLink> {
  const body = linkGithubPullRequestRequestSchema.parse(input);
  const response = await apiRequest(`/issues/${encodeURIComponent(key)}/github/pull-requests`, {
    body: JSON.stringify(body),
    headers: jsonHeaders,
    method: "POST",
    token,
  });
  await assertOk(response, "github_pr_link_failed");
  return (await response.json()) as GithubPullRequestLink;
}

export async function getSlackSettings(token: string): Promise<SlackSettingsState> {
  return getJson("/settings/slack", token, slackSettingsResponseSchema, "slack_settings_failed");
}

export async function updateSlackSettings(
  token: string,
  input: SlackSettingsState["settings"],
): Promise<SlackSettingsState> {
  return sendJsonAndParse("/settings/slack", {
    errorCode: "slack_settings_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateSlackSettingsRequestSchema,
    responseSchema: slackSettingsResponseSchema,
    token,
  });
}

export async function listAdminUsers(token: string): Promise<AdminUser[]> {
  return (await getJson("/settings/users", token, adminUserListResponseSchema, "users_failed"))
    .items;
}

export async function createAdminUser(
  token: string,
  input: {
    email: string;
    name: string;
    role: AdminUser["role"];
    status?: AdminUser["status"];
    googleSubject?: string | null;
    avatarUrl?: string | null;
  },
): Promise<AdminUser> {
  return sendJsonAndParse("/settings/users", {
    errorCode: "user_create_failed",
    input,
    method: "POST",
    requestSchema: createAdminUserRequestSchema,
    responseSchema: adminUserSchema,
    token,
  });
}

export async function preProvisionAdminUser(
  token: string,
  input: {
    email: string;
    name?: string;
    role: AdminUser["role"];
    googleSubject?: string | null;
    avatarUrl?: string | null;
  },
): Promise<AdminUser> {
  return sendJsonAndParse("/settings/users/pre-provision", {
    errorCode: "user_pre_provision_failed",
    input,
    method: "POST",
    requestSchema: preProvisionAdminUserRequestSchema,
    responseSchema: adminUserSchema,
    token,
  });
}

export async function updateAdminUser(
  token: string,
  id: string,
  input: Partial<
    Pick<
      AdminUser,
      "email" | "name" | "role" | "status" | "googleSubject" | "lastLoginAt" | "avatarUrl"
    >
  >,
): Promise<AdminUser> {
  return sendJsonAndParse(`/settings/users/${encodeURIComponent(id)}`, {
    errorCode: "user_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateAdminUserRequestSchema,
    responseSchema: adminUserSchema,
    token,
  });
}

export async function listApiKeys(token: string): Promise<ApiKey[]> {
  return (await getJson("/settings/api-keys", token, apiKeyListResponseSchema, "api_keys_failed"))
    .items;
}

export async function createApiKey(
  token: string,
  input: { name: string },
): Promise<{ apiKey: ApiKey; token: string }> {
  return sendJsonAndParse("/settings/api-keys", {
    errorCode: "api_key_create_failed",
    input,
    method: "POST",
    requestSchema: createApiKeyRequestSchema,
    responseSchema: createApiKeyResponseSchema,
    token,
  });
}

export async function revokeApiKey(token: string, id: string): Promise<void> {
  await deleteRequest(
    `/settings/api-keys/${encodeURIComponent(id)}`,
    token,
    "api_key_revoke_failed",
  );
}

export async function listProducts(token: string): Promise<Product[]> {
  return (await getJson("/products", token, productsResponseSchema, "products_failed")).items;
}

export async function createProduct(
  token: string,
  input: { key: string; name: string; ownerUserId?: string | null },
): Promise<Product> {
  return sendJsonAndParse("/products", {
    errorCode: "product_create_failed",
    input,
    method: "POST",
    requestSchema: createProductRequestSchema,
    responseSchema: productSchema,
    token,
  });
}

export async function updateProduct(
  token: string,
  id: string,
  input: { key?: string; name?: string; ownerUserId?: string | null },
): Promise<Product> {
  return sendJsonAndParse(`/products/${encodeURIComponent(id)}`, {
    errorCode: "product_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateProductRequestSchema,
    responseSchema: productSchema,
    token,
  });
}

export async function deleteProduct(token: string, id: string): Promise<void> {
  await deleteRequest(`/products/${encodeURIComponent(id)}`, token, "product_delete_failed");
}

export async function listClients(token: string): Promise<Client[]> {
  return (await getJson("/clients", token, clientsResponseSchema, "clients_failed")).items;
}

export async function createClient(
  token: string,
  input: { key: string; name: string },
): Promise<Client> {
  return sendJsonAndParse("/clients", {
    errorCode: "client_create_failed",
    input,
    method: "POST",
    requestSchema: createClientRequestSchema,
    responseSchema: clientSchema,
    token,
  });
}

export async function updateClient(
  token: string,
  id: string,
  input: { key?: string; name?: string },
): Promise<Client> {
  return sendJsonAndParse(`/clients/${encodeURIComponent(id)}`, {
    errorCode: "client_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateClientRequestSchema,
    responseSchema: clientSchema,
    token,
  });
}

export async function deleteClient(token: string, id: string): Promise<void> {
  await deleteRequest(`/clients/${encodeURIComponent(id)}`, token, "client_delete_failed");
}

export async function listProjects(token: string): Promise<Project[]> {
  return (await getJson("/projects", token, projectsResponseSchema, "projects_failed")).items;
}

export async function createProject(
  token: string,
  input: {
    key: string;
    name: string;
    clientAccountId?: string | null;
    productId?: string | null;
    status?: Project["status"];
  },
): Promise<Project> {
  return sendJsonAndParse("/projects", {
    errorCode: "project_create_failed",
    input,
    method: "POST",
    requestSchema: createProjectRequestSchema,
    responseSchema: projectSchema,
    token,
  });
}

export async function updateProject(
  token: string,
  id: string,
  input: {
    key?: string;
    name?: string;
    clientAccountId?: string | null;
    productId?: string | null;
    status?: Project["status"];
  },
): Promise<Project> {
  return sendJsonAndParse(`/projects/${encodeURIComponent(id)}`, {
    errorCode: "project_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateProjectRequestSchema,
    responseSchema: projectSchema,
    token,
  });
}

export async function deleteProject(token: string, id: string): Promise<void> {
  await deleteRequest(`/projects/${encodeURIComponent(id)}`, token, "project_delete_failed");
}

export async function listResearchTracks(token: string): Promise<ResearchTrack[]> {
  return (
    await getJson("/research-tracks", token, researchTracksResponseSchema, "research_tracks_failed")
  ).items;
}

export async function createResearchTrack(
  token: string,
  input: { key: string; name: string; status?: ResearchTrack["status"] },
): Promise<ResearchTrack> {
  return sendJsonAndParse("/research-tracks", {
    errorCode: "research_track_create_failed",
    input,
    method: "POST",
    requestSchema: createResearchTrackRequestSchema,
    responseSchema: researchTrackSchema,
    token,
  });
}

export async function updateResearchTrack(
  token: string,
  id: string,
  input: { key?: string; name?: string; status?: ResearchTrack["status"] },
): Promise<ResearchTrack> {
  return sendJsonAndParse(`/research-tracks/${encodeURIComponent(id)}`, {
    errorCode: "research_track_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateResearchTrackRequestSchema,
    responseSchema: researchTrackSchema,
    token,
  });
}

export async function deleteResearchTrack(token: string, id: string): Promise<void> {
  await deleteRequest(
    `/research-tracks/${encodeURIComponent(id)}`,
    token,
    "research_track_delete_failed",
  );
}

export async function listChapters(token: string): Promise<Chapter[]> {
  return (await getJson("/chapters", token, chaptersResponseSchema, "chapters_failed")).items;
}

export async function createChapter(
  token: string,
  input: { key: string; name: string; leadUserId?: string | null },
): Promise<Chapter> {
  return sendJsonAndParse("/chapters", {
    errorCode: "chapter_create_failed",
    input,
    method: "POST",
    requestSchema: createChapterRequestSchema,
    responseSchema: chapterItemSchema,
    token,
  });
}

export async function updateChapter(
  token: string,
  id: string,
  input: { key?: string; name?: string; leadUserId?: string | null },
): Promise<Chapter> {
  return sendJsonAndParse(`/chapters/${encodeURIComponent(id)}`, {
    errorCode: "chapter_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateChapterRequestSchema,
    responseSchema: chapterItemSchema,
    token,
  });
}

export async function deleteChapter(token: string, id: string): Promise<void> {
  await deleteRequest(`/chapters/${encodeURIComponent(id)}`, token, "chapter_delete_failed");
}

export async function listCycles(token: string): Promise<Cycle[]> {
  return (await getJson("/cycles", token, cyclesResponseSchema, "cycles_failed")).items;
}

export async function createCycle(
  token: string,
  input: {
    key: string;
    name: string;
    startsAt: string;
    endsAt: string;
    status?: Cycle["status"];
  },
): Promise<Cycle> {
  return sendJsonAndParse("/cycles", {
    errorCode: "cycle_create_failed",
    input,
    method: "POST",
    requestSchema: createCycleRequestSchema,
    responseSchema: cycleItemSchema,
    token,
  });
}

export async function updateCycle(
  token: string,
  id: string,
  input: {
    key?: string;
    name?: string;
    startsAt?: string;
    endsAt?: string;
    status?: Cycle["status"];
  },
): Promise<Cycle> {
  return sendJsonAndParse(`/cycles/${encodeURIComponent(id)}`, {
    errorCode: "cycle_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateCycleRequestSchema,
    responseSchema: cycleItemSchema,
    token,
  });
}

export async function deleteCycle(token: string, id: string): Promise<void> {
  await deleteRequest(`/cycles/${encodeURIComponent(id)}`, token, "cycle_delete_failed");
}

export async function listRoadmap(token: string): Promise<RoadmapMilestone[]> {
  return (await getJson("/roadmap", token, roadmapResponseSchema, "roadmap_failed")).items;
}

export async function createRoadmapMilestone(
  token: string,
  input: {
    key: string;
    name: string;
    targetDate?: string | null;
    status?: RoadmapMilestone["status"];
  },
): Promise<RoadmapMilestone> {
  return sendJsonAndParse("/roadmap", {
    errorCode: "roadmap_create_failed",
    input,
    method: "POST",
    requestSchema: createRoadmapMilestoneRequestSchema,
    responseSchema: roadmapMilestoneSchema,
    token,
  });
}

export async function updateRoadmapMilestone(
  token: string,
  id: string,
  input: {
    key?: string;
    name?: string;
    targetDate?: string | null;
    status?: RoadmapMilestone["status"];
  },
): Promise<RoadmapMilestone> {
  return sendJsonAndParse(`/roadmap/${encodeURIComponent(id)}`, {
    errorCode: "roadmap_update_failed",
    input,
    method: "PATCH",
    requestSchema: updateRoadmapMilestoneRequestSchema,
    responseSchema: roadmapMilestoneSchema,
    token,
  });
}

export async function deleteRoadmapMilestone(token: string, id: string): Promise<void> {
  await deleteRequest(`/roadmap/${encodeURIComponent(id)}`, token, "roadmap_delete_failed");
}

export async function listContextUsers(token: string): Promise<ContextUser[]> {
  return (await getJson("/users", token, contextUsersResponseSchema, "context_users_failed")).items;
}

export async function listIssueLabels(token: string): Promise<IssueLabel[]> {
  return (await getJson("/labels", token, issueLabelsResponseSchema, "issue_labels_failed")).items;
}

export async function getAnalytics(token: string): Promise<AnalyticsRollup> {
  return getJson("/analytics", token, analyticsRollupSchema, "analytics_failed");
}

export async function assignIssueUser(
  token: string,
  key: string,
  input: { assigneeUserId: string | null },
): Promise<Issue> {
  return sendJsonAndParse(`/issues/${encodeURIComponent(key)}/assign`, {
    errorCode: "issue_user_assign_failed",
    input,
    method: "POST",
    requestSchema: assignIssueRequestSchema,
    responseSchema: issueItemSchema,
    token,
  });
}

async function assertOk(response: Response, code: string): Promise<void> {
  if (!response.ok) {
    throw new ApiError(code, response.status, await readErrorMessage(response));
  }
}

async function readErrorMessage(response: Response): Promise<string | undefined> {
  const text = await response.text();
  if (text.trim().length === 0) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(text) as { error?: { message?: unknown } | string };
    if (typeof parsed.error === "string") {
      return parsed.error;
    }
    if (typeof parsed.error?.message === "string") {
      return parsed.error.message;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

async function getJson<T>(
  path: string,
  token: string | null,
  responseSchema: Parser<T>,
  errorCode: string,
): Promise<T> {
  const response = await apiRequest(path, { token });
  await assertOk(response, errorCode);
  return responseSchema.parse(await response.json());
}

async function postAndParse<TOutput>(
  path: string,
  token: string | null,
  responseSchema: Parser<TOutput>,
  errorCode: string,
): Promise<TOutput> {
  const response = await apiRequest(path, { method: "POST", token });
  await assertOk(response, errorCode);
  return responseSchema.parse(await response.json());
}

async function sendJsonAndParse<TInput, TOutput>(
  path: string,
  options: JsonRequestOptions<TInput, TOutput>,
): Promise<TOutput> {
  const body = options.requestSchema.parse(options.input);
  const response = await apiRequest(path, {
    body: JSON.stringify(body),
    headers: jsonHeaders,
    method: options.method,
    token: options.token,
  });
  await assertOk(response, options.errorCode);
  return options.responseSchema.parse(await response.json());
}

async function sendJson<TInput>(
  path: string,
  options: JsonRequestWithoutResponseOptions<TInput>,
): Promise<void> {
  const body = options.requestSchema.parse(options.input);
  const response = await apiRequest(path, {
    body: JSON.stringify(body),
    headers: jsonHeaders,
    method: options.method,
    token: options.token,
  });
  await assertOk(response, options.errorCode);
}

async function deleteRequest(path: string, token: string | null, errorCode: string): Promise<void> {
  const response = await apiRequest(path, { method: "DELETE", token });
  await assertOk(response, errorCode);
}

function buildQueryPath(path: string, values: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  return params.size > 0 ? `${path}?${params.toString()}` : path;
}

export async function apiRequest(
  path: string,
  options: RequestInit & { token: string | null },
): Promise<Response> {
  const headers = new Headers(options.headers);
  if (options.token !== null && options.token !== browserSessionToken) {
    headers.set("authorization", `Bearer ${options.token}`);
  }

  return fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
    headers,
  });
}
